import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Users, PhoneCall, Sparkles, AlertTriangle, TrendingUp, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import MeetingHubCard from "@/components/MeetingHubCard";

function MeetingRow({ m, showOwner }) {
  const navigate = useNavigate();
  const Icon = m.kind === "call" ? PhoneCall : Users;
  const st = { processing: "En cours d'analyse", ready: "À valider", applied: "Validé", error: "Erreur" }[m.status] || m.status;
  return (
    <button onClick={() => navigate(`/reunions/${m.id}`)} data-testid={`meeting-row-${m.id}`} className="w-full text-left klt-glow-card rounded-2xl p-4 flex items-start gap-3 hover:-translate-y-0.5 transition-transform">
      <div className="w-10 h-10 rounded-xl bg-[#3B82F6]/15 flex items-center justify-center shrink-0"><Icon className="w-5 h-5 text-[#60A5FA]" /></div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold truncate">{m.report?.title || m.title || (m.kind === "call" ? "Appel" : "Réunion")}</p>
        <p className="text-xs text-muted-foreground">{showOwner && `${m.ownerName} · `}{new Date(m.createdAt).toLocaleString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}{m.durationSec ? ` · ${Math.round(m.durationSec / 60)} min` : ""}</p>
        {m.report?.summary?.[0] && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{m.report.summary[0]}</p>}
      </div>
      <span className={`text-[10px] font-bold px-2 py-1 rounded-full shrink-0 ${m.status === "ready" ? "bg-amber-400/20 text-amber-400" : m.status === "applied" ? "bg-emerald-500/15 text-emerald-400" : "bg-secondary text-muted-foreground"}`}>{st}</span>
    </button>
  );
}

function TeamDigest() {
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => { api.get("/meetings/team/digest").then(({ data }) => setD(data)).catch(() => {}); }, []);
  const gen = async () => {
    setLoading(true);
    try { const fd = new FormData(); fd.append("days", 7); const { data } = await api.post("/meetings/team/digest", fd); setD(data); } catch { toast.error("Synthèse impossible"); } finally { setLoading(false); }
  };
  return (
    <div className="klt-glow-card rounded-3xl p-5" data-testid="team-digest">
      <div className="flex items-center justify-between gap-3">
        <p className="font-heading font-extrabold flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#60A5FA]" /> Synthèse IA de la semaine</p>
        <button onClick={gen} disabled={loading} data-testid="team-digest-btn" className="h-9 px-4 rounded-full klt-fab-neon text-white text-xs font-semibold inline-flex items-center gap-1.5">{loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />} {d?.overview ? "Actualiser" : "Générer"}</button>
      </div>
      {d?.overview && (
        <div className="mt-4 space-y-4 text-sm" data-testid="team-digest-content">
          <p>{d.overview}</p>
          {d.alerts?.length > 0 && <div className="rounded-2xl bg-rose-500/10 p-3"><p className="font-semibold text-rose-400 flex items-center gap-1.5 mb-1"><AlertTriangle className="w-4 h-4" /> À traiter</p>{d.alerts.map((a, i) => <p key={i}>• {a}</p>)}</div>}
          {d.topOpportunities?.length > 0 && <div className="rounded-2xl bg-emerald-500/10 p-3"><p className="font-semibold text-emerald-400 flex items-center gap-1.5 mb-1"><TrendingUp className="w-4 h-4" /> Opportunités</p>{d.topOpportunities.map((a, i) => <p key={i}>• {a}</p>)}</div>}
          <div className="grid sm:grid-cols-2 gap-3">{(d.members || []).map((mb, i) => (
            <div key={i} className="rounded-2xl bg-secondary/50 p-3" data-testid={`digest-member-${i}`}>
              <p className="font-semibold">{mb.name} <span className="text-xs text-muted-foreground">· {mb.meetings} CR</span></p>
              {(mb.highlights || []).map((h, j) => <p key={j} className="text-muted-foreground">• {h}</p>)}
              {(mb.risks || []).map((h, j) => <p key={j} className="text-rose-400">⚠ {h}</p>)}
            </div>))}</div>
          {d.advice && <p className="italic text-[#60A5FA]">{d.advice}</p>}
        </div>
      )}
    </div>
  );
}

export default function ReunionsPage() {
  const { user } = useAuth();
  const manager = user?.role === "MANAGER" || !!user?.isPlatformAdmin;
  const [scope, setScope] = useState("mine");
  const [list, setList] = useState([]);
  const [owner, setOwner] = useState("");
  useEffect(() => { api.get("/meetings", { params: { scope } }).then(({ data }) => setList(data)).catch(() => {}); }, [scope]);
  const owners = [...new Map(list.map((m) => [m.ownerId, m.ownerName])).entries()];
  const shown = owner ? list.filter((m) => m.ownerId === owner) : list;
  const tab = (k, l) => <button onClick={() => { setScope(k); setOwner(""); }} data-testid={`reunions-tab-${k}`} className={`px-4 h-9 rounded-full text-sm font-semibold ${scope === k ? "bg-card shadow" : "text-muted-foreground"}`}>{l}</button>;
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-4" data-testid="reunions-page">
      <h1 className="font-heading text-3xl font-extrabold">Comptes rendus</h1>
      <div className="grid grid-cols-2 gap-3"><MeetingHubCard /></div>
      {manager && <div className="inline-flex bg-secondary/60 rounded-full p-1">{tab("mine", "Les miens")}{tab("team", "Équipe")}</div>}
      {scope === "team" && <TeamDigest />}
      {scope === "team" && owners.length > 1 && (
        <div className="flex gap-2 flex-wrap" data-testid="owner-filter">
          <button onClick={() => setOwner("")} className={`px-3 h-8 rounded-full text-xs font-semibold ${!owner ? "bg-[#3B82F6] text-white" : "bg-secondary"}`}>Tous</button>
          {owners.map(([id, n]) => <button key={id} onClick={() => setOwner(id)} data-testid={`owner-chip-${id}`} className={`px-3 h-8 rounded-full text-xs font-semibold ${owner === id ? "bg-[#3B82F6] text-white" : "bg-secondary"}`}>{n}</button>)}
        </div>
      )}
      <div className="space-y-2" data-testid="meetings-list">
        {shown.length === 0 ? <p className="text-sm text-muted-foreground text-center py-8">Aucun compte rendu pour l'instant.</p> : shown.map((m) => <MeetingRow key={m.id} m={m} showOwner={scope === "team"} />)}
      </div>
    </div>
  );
}
